import { useEffect, useState } from "react";
import { Filter, RotateCcw } from "lucide-react";

import { Card } from "../../../shared/card/card.tsx";
import { Button, Select } from "../../../ui/index.ts";
import { useEmployeeNameOptions } from "../../../../hooks/use-employee-name-options.ts";
import type {
  InsuranceFilters,
  InsuranceStatusFilter,
} from "../types.ts";

import styles from "./insurance-filters.module.css";

const STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REVOKED", label: "Revoked" },
];

export function InsuranceFiltersCard({
  value,
  loading,
  onApply,
  onReset,
}: {
  value: InsuranceFilters;
  loading?: boolean;
  onApply: (filters: InsuranceFilters) => void;
  onReset: () => void;
}) {
  const employees = useEmployeeNameOptions();
  const [employeeId, setEmployeeId] = useState(value.employeeId);
  const [status, setStatus] = useState<InsuranceStatusFilter>(value.status);

  useEffect(() => {
    setEmployeeId(value.employeeId);
    setStatus(value.status);
  }, [value]);

  useEffect(() => {
    const appliedFiltersExist = Boolean(value.employeeId || value.status);
    if (!employeeId && !status && appliedFiltersExist) {
      onReset();
    }
  }, [employeeId, onReset, status, value.employeeId, value.status]);

  const reset = () => {
    setEmployeeId("");
    setStatus("");
    onReset();
  };

  return (
    <Card className={styles.card}>
      <div className={styles.row}>
        <Select
          id="insurance-tracking-employee"
          label="Employee Name"
          placeholder={employees.loading ? "Loading employees…" : "Select Employee"}
          options={employees.options}
          value={employeeId}
          onChange={setEmployeeId}
          searchable
          isClearable
          disabled={employees.loading}
          error={employees.error ?? undefined}
        />
        <Select
          id="insurance-tracking-status"
          label="Status"
          placeholder="Select Status"
          options={STATUS_OPTIONS}
          value={status}
          onChange={(next) => setStatus(next as InsuranceStatusFilter)}
          isClearable
        />
        <div className={styles.actions}>
          <Button
            type="button"
            variant="accent"
            disabled={loading}
            startIcon={<Filter size={15} aria-hidden />}
            onClick={() => onApply({ employeeId, status })}
          >
            Apply Filter
          </Button>
          <Button
            type="button"
            variant="light-grey"
            disabled={loading}
            startIcon={<RotateCcw size={15} aria-hidden />}
            onClick={reset}
          >
            Clear Filter
          </Button>
        </div>
      </div>
    </Card>
  );
}
